import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { fetchMe } from "@/api";
import { AuthUser } from "@/types";

interface AuthContextType {
  user: AuthUser | undefined;
  loading: boolean;
  isAuthenticated: boolean;
  refreshUser: (userFromLogin?: AuthUser) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: undefined,
  loading: true,
  isAuthenticated: false,
  refreshUser: async () => {},
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const refreshUser = async (userFromLogin?: AuthUser) => {
    if (userFromLogin) {
      setUser(userFromLogin);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const data = await fetchMe();
      setUser(data);
    } catch {
      setUser(undefined);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshUser();
  }, []);

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{ user, loading, isAuthenticated, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
